import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const QRCodeModal: React.FC<QRCodeModalProps> = ({ isOpen, onClose }) => {
  // Close on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
            <motion.div
              className="relative bg-black rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="qr-modal-title"
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white transition-colors"
                aria-label="Close modal"
              >
                <span className="text-2xl leading-none">×</span>
              </button>

              {/* Content - Responsive Padding */}
              <div className="p-4 sm:p-6 lg:p-8 text-center">
                {/* Title - Responsive Typography */}
                <h2
                  id="qr-modal-title"
                  className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2"
                >
                  Book Your Appointment
                </h2>
                
                {/* Description - Responsive Typography */}
                <p className="text-sm sm:text-base text-gray-300 mb-6">
                  Scan the QR code to book your Appointment
                </p>

                {/* QR Code Image - Responsive Size */}
                <div className="mb-4">
                  <img
                    src="/Qr.png"
                    alt="QR Code for booking appointment"
                    className="w-40 sm:w-44 lg:w-52 mx-auto rounded-lg"
                  />
                </div>

                {/* Helper Text - Responsive Typography */}
                <p className="text-xs sm:text-sm text-gray-400 mb-6">
                  Use your phone's camera to scan the code
                </p>

                {/* Divider - Responsive Typography */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-1 h-px bg-gray-700"></div>
                  <span className="text-xs sm:text-sm text-gray-400 font-medium">OR</span>
                  <div className="flex-1 h-px bg-gray-700"></div>
                </div>

                {/* Phone Number - Responsive Typography */}
                <a
                  href="tel:+17325816834"
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-colors"
                >
                  <span className="material-symbols-outlined text-lg sm:text-xl">phone</span>
                  <span className="text-sm sm:text-base">(732) 581-6834</span>
                </a>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default QRCodeModal;
